import { useEffect, useState } from 'react';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Chip,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
} from '../../../core';
import { DatePickerInput } from '../../../dates';
import { SURFACE_COLORS } from '../surface-colors';
import {
  combineDateTime,
  formatAnswerLabel,
  groupFields,
  halfHourTimes,
  hasAnyAnswer,
  hiddenAnswerKeys,
  maxDateFor,
  splitDateTime,
  stepsFor,
  toggleChoice,
  todayIsoDate,
  visibleFields,
  yesterdayIsoDate,
  type ClaimReportAnswers,
  type ClaimReportField,
} from './claimReportRules';
import { SurfaceHeader } from '../SurfaceHeader';

/** Design px -> rem, against the app's 12px root — same helper every other
 *  console component defines locally (panelLayout.ts, mfa.styles.ts, …). */
const rem = (value: number): string => `${value / 12}rem`;

export interface ClaimReportPolicy {
  readonly policyNumber: string;
  readonly productType: string;
  readonly companyName: string;
}

export interface ClaimReportSituation {
  readonly id: string;
  readonly label: string;
  readonly description: string;
}

export type ClaimReportPhase = 'choices' | 'details';

export interface ClaimReportProps {
  readonly phase: ClaimReportPhase;
  readonly title?: string;
  readonly policy: ClaimReportPolicy | null;

  /** `choices` only — a Radio.Card-style single pick, stored at
   *  `/answers/situationId`. Absent/empty in `details`. */
  readonly situations?: readonly ClaimReportSituation[];
  readonly fields?: readonly ClaimReportField[];
  /** The card's one action: a CHAT-SEND, exactly like
   *  `AskQuestions:SubmitAnswers`. Fires with no arguments — the picks are
   *  read back off `/answers` by the host, via the action's own context
   *  binding, not passed through this callback. */
  readonly onSend?: () => void;

  /** Current `/answers` off the host data model, read non-reactively by the
   *  binder the same way it reads `/submitted` for AskQuestions — used to
   *  prefill choices/details controls and to resume mid-wizard on a
   *  re-render. */
  readonly initialAnswers?: ClaimReportAnswers;
  /** Writes a value back to the host data model at an absolute path — the
   *  card's one channel for anything `onSend`'s context reads
   *  (`/answers/<key>`). */
  readonly onWrite?: (path: string, value: unknown) => void;

  /** Written by the client, never by the agent: true once this surface is no
   *  longer the latest turn, or once Send has fired — freezing every control
   *  so a stale or already-sent card can't be re-answered. Read directly off
   *  `/locked`, the same flag AskQuestions freezes on. */
  readonly locked?: boolean;
  /** Written by the client at Send time, read directly off `/submitted` (no
   *  agent binding, same as AskQuestions' `readSubmitted`) — distinguishes a
   *  card that was sent from one that merely went stale unanswered. */
  readonly submitted?: boolean;
}

/** One row of single/multi-pick chips. Shared by RADIO/SELECT/BOOLEAN/CHECKS
 *  so every choice control on the card reads the same way. */
function ChipRow({ children }: { readonly children: React.ReactNode }) {
  return (
    <Group gap={rem(8)} wrap="wrap">
      {children}
    </Group>
  );
}

function RadioLikeControl({
  options,
  value,
  disabled,
  onChange,
}: {
  readonly options: readonly {
    readonly code: string;
    readonly label: string;
  }[];
  readonly value: string;
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
}) {
  return (
    <Chip.Group value={value} onChange={(next) => onChange(next as string)}>
      <ChipRow>
        {options.map((option) => (
          <Chip key={option.code} value={option.code} disabled={disabled}>
            {option.label}
          </Chip>
        ))}
      </ChipRow>
    </Chip.Group>
  );
}

/** A stored `true`/`false` (or unanswered) as the Chip.Group value it drives
 *  — built from statements rather than a nested ternary. */
function booleanChipValue(value: unknown): string {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

function BooleanControl({
  value,
  disabled,
  onChange,
}: {
  readonly value: unknown;
  readonly disabled?: boolean;
  readonly onChange: (value: boolean) => void;
}) {
  const current = booleanChipValue(value);
  return (
    <Chip.Group value={current} onChange={(next) => onChange(next === 'true')}>
      <ChipRow>
        <Chip value="true" disabled={disabled}>
          Yes
        </Chip>
        <Chip value="false" disabled={disabled}>
          No
        </Chip>
      </ChipRow>
    </Chip.Group>
  );
}

function ChecksControl({
  options,
  value,
  disabled,
  onChange,
}: {
  readonly options: readonly {
    readonly code: string;
    readonly label: string;
  }[];
  readonly value: readonly string[];
  readonly disabled?: boolean;
  readonly onChange: (value: string[]) => void;
}) {
  return (
    <ChipRow>
      {options.map((option) => (
        <Chip
          key={option.code}
          checked={value.includes(option.code)}
          disabled={disabled}
          onChange={(checked) =>
            onChange(toggleChoice(value, option.code, checked))
          }
        >
          {option.label}
        </Chip>
      ))}
    </ChipRow>
  );
}

function DateControl({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: ClaimReportField;
  readonly value: unknown;
  readonly disabled?: boolean;
  readonly onChange: (value: string | undefined) => void;
}) {
  const current = typeof value === 'string' ? value : '';
  const today = todayIsoDate();
  const yesterday = yesterdayIsoDate();
  return (
    <Stack gap={rem(6)}>
      <ChipRow>
        <Chip
          checked={current === today}
          disabled={disabled}
          onChange={(checked) => checked && onChange(today)}
        >
          Today
        </Chip>
        <Chip
          checked={current === yesterday}
          disabled={disabled}
          onChange={(checked) => checked && onChange(yesterday)}
        >
          Yesterday
        </Chip>
      </ChipRow>
      <DatePickerInput
        placeholder="Pick a date"
        value={current || null}
        maxDate={maxDateFor(field)}
        disabled={disabled}
        onChange={(next) =>
          onChange(typeof next === 'string' ? next : undefined)
        }
      />
    </Stack>
  );
}

const TIME_CHIPS = halfHourTimes();

function DateTimeControl({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: ClaimReportField;
  readonly value: unknown;
  readonly disabled?: boolean;
  readonly onChange: (value: string | undefined) => void;
}) {
  const parts = splitDateTime(value);
  const setDate = (date: string | undefined) =>
    onChange(combineDateTime(date, parts.time));
  const setTime = (time: string) => onChange(combineDateTime(parts.date, time));
  return (
    <Stack gap={rem(6)}>
      <DatePickerInput
        placeholder="Pick a date"
        value={parts.date || null}
        maxDate={maxDateFor(field)}
        disabled={disabled}
        onChange={(next) =>
          setDate(typeof next === 'string' ? next : undefined)
        }
      />
      <Chip.Group
        value={parts.time ?? ''}
        onChange={(next) => setTime(next as string)}
      >
        <Group gap={rem(6)} wrap="wrap">
          {TIME_CHIPS.map((time) => (
            <Chip key={time} value={time} size="xs" disabled={disabled}>
              {time}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    </Stack>
  );
}

/** Routes to the right control for a field's type. No TextInput/Textarea/
 *  NumberInput/money input is offered anywhere on this card — every field
 *  type the contract defines is a choice control. */
function FieldControl({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: ClaimReportField;
  readonly value: unknown;
  readonly disabled?: boolean;
  readonly onChange: (value: unknown) => void;
}) {
  if (field.type === 'RADIO' || field.type === 'SELECT') {
    return (
      <RadioLikeControl
        options={field.options}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }
  if (field.type === 'BOOLEAN') {
    return (
      <BooleanControl value={value} disabled={disabled} onChange={onChange} />
    );
  }
  if (field.type === 'CHECKS') {
    return (
      <ChecksControl
        options={field.options}
        value={Array.isArray(value) ? (value as string[]) : []}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }
  if (field.type === 'DATE') {
    return (
      <DateControl
        field={field}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }
  return (
    <DateTimeControl
      field={field}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

/** One field row: label, optional hint, then its control. No "required"
 *  marker — the card doesn't enforce required fields, so flagging one as
 *  mandatory here would promise a gate that doesn't exist. */
function FieldRow({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: ClaimReportField;
  readonly value: unknown;
  readonly disabled?: boolean;
  readonly onChange: (value: unknown) => void;
}) {
  return (
    <Stack gap={rem(4)}>
      <Text size="sm" fw={600}>
        {field.label}
      </Text>
      {field.hint && (
        <Text size="xs" c="dimmed">
          {field.hint}
        </Text>
      )}
      <FieldControl
        field={field}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
    </Stack>
  );
}

/** Thin step indicator — "Step x of n" plus a slim progress bar. */
function StepProgress({
  step,
  total,
}: {
  readonly step: number;
  readonly total: number;
}) {
  if (total <= 1) return null;
  return (
    <Stack gap={rem(4)} mt={rem(12)}>
      <Text size="xs" c="dimmed">
        Step {step + 1} of {total}
      </Text>
      <Progress value={((step + 1) / total) * 100} size={rem(4)} radius="xl" />
    </Stack>
  );
}

/** Hook shared by both phases: answers state prefilled from `/answers`,
 *  mirrored back through `onWrite` on every pick, and pruned of any field
 *  that has hidden since it was answered (see `hiddenAnswerKeys`) so
 *  `/answers` never carries a stale pick into the chat-send message. */
function useClaimReportAnswers(
  fields: readonly ClaimReportField[],
  initialAnswers: ClaimReportAnswers,
  onWrite: (path: string, value: unknown) => void
) {
  const [answers, setAnswers] = useState<ClaimReportAnswers>(initialAnswers);
  const situationId =
    typeof answers.situationId === 'string' ? answers.situationId : undefined;

  const setAnswer = (key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    onWrite(`/answers/${key}`, value);
  };

  useEffect(() => {
    const stale = hiddenAnswerKeys(fields, answers, situationId);
    if (stale.length === 0) return;
    setAnswers((prev) => {
      const next = { ...prev };
      stale.forEach((key) => delete next[key]);
      return next;
    });
    stale.forEach((key) => onWrite(`/answers/${key}`, undefined));
    // Only re-run when the pruning inputs change; `onWrite` is expected
    // stable-enough for this effect's purpose (mirrors the AskQuestions binder
    // contract) and including it would re-run on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, answers, situationId]);

  return { answers, situationId, setAnswer };
}

/** Frozen view once the card is locked (Send fired, or the turn moved on): a
 *  settled notice plus the picks themselves, read from what's still visible
 *  — the same shape AskQuestions freezes into, generalised to this card's
 *  situation + field answers rather than its clarifications. */
function FrozenSummary({
  submitted,
  situations,
  situationId,
  visible,
  answers,
}: {
  readonly submitted: boolean;
  readonly situations: readonly ClaimReportSituation[];
  readonly situationId: string | undefined;
  readonly visible: readonly ClaimReportField[];
  readonly answers: ClaimReportAnswers;
}) {
  const picks: { label: string; value: string }[] = [];
  const situation = situations.find((entry) => entry.id === situationId);
  if (situation) picks.push({ label: 'Situation', value: situation.label });
  visible.forEach((field) => {
    const value = formatAnswerLabel(field, answers[field.key]);
    if (value) picks.push({ label: field.label, value });
  });

  return (
    <Stack gap={rem(10)} mt={rem(12)}>
      <Group
        justify="center"
        align="center"
        gap={rem(8)}
        wrap="nowrap"
        py={rem(10)}
        px={rem(16)}
        style={{
          borderRadius: rem(8),
          backgroundColor: submitted
            ? SURFACE_COLORS.settledSubmitted
            : SURFACE_COLORS.settledSkipped,
        }}
      >
        {submitted && (
          <FontAwesomeIcon
            icon={faCheck}
            style={{
              width: rem(16),
              height: rem(16),
              color: 'var(--mantine-color-green-6)',
            }}
          />
        )}
        <Text size="sm" ta="center">
          {submitted ? 'Sent' : 'Skipped'}
        </Text>
      </Group>
      {submitted && picks.length > 0 && (
        <Stack gap={rem(6)}>
          {picks.map((pick, index) => (
            <Group
              key={`${pick.label}-${index}`}
              justify="space-between"
              wrap="nowrap"
            >
              <Text size="sm" c="dimmed">
                {pick.label}
              </Text>
              <Text size="sm">{pick.value}</Text>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** Renders the "choices" phase: an optional situation pick, then the field
 *  steps, one screen at a time, ending in Send. The existing-claims question
 *  is an ordinary RADIO field (`confirmedNewIncident`) built by chat-agent —
 *  there is no special gate step here. */
function ChoicesPhase({
  situations,
  fields,
  initialAnswers,
  locked,
  onSend,
  onWrite,
}: {
  readonly situations: readonly ClaimReportSituation[];
  readonly fields: readonly ClaimReportField[];
  readonly initialAnswers: ClaimReportAnswers;
  readonly locked: boolean;
  readonly onSend: (() => void) | undefined;
  readonly onWrite: (path: string, value: unknown) => void;
}) {
  const { answers, situationId, setAnswer } = useClaimReportAnswers(
    fields,
    initialAnswers,
    onWrite
  );
  const [step, setStep] = useState(0);

  const visible = visibleFields(fields, answers, situationId);
  const fieldSteps = stepsFor(visible);
  const hasSituations = situations.length > 0;
  const virtualSteps: (
    | { readonly kind: 'situation' }
    | { readonly kind: 'field'; readonly step: string }
  )[] = hasSituations
    ? [
        { kind: 'situation' },
        ...fieldSteps.map((s) => ({ kind: 'field' as const, step: s })),
      ]
    : fieldSteps.map((s) => ({ kind: 'field' as const, step: s }));
  const total = virtualSteps.length;
  const current = virtualSteps[Math.min(step, total - 1)];
  const isLast = step >= total - 1;
  const canSend = hasAnyAnswer(answers);

  const advance = () => setStep((prev) => Math.min(prev + 1, total - 1));
  const goBack = () => setStep((prev) => Math.max(prev - 1, 0));

  if (current?.kind === 'situation') {
    return (
      <Stack gap={rem(12)} mt={rem(12)}>
        <Text size="sm" fw={600}>
          What kind of incident?
        </Text>
        <ChipRow>
          {situations.map((situation) => (
            <Chip
              key={situation.id}
              value={situation.id}
              checked={situationId === situation.id}
              disabled={locked}
              onChange={(checked) => {
                if (!checked) return;
                setAnswer('situationId', situation.id);
              }}
            >
              {situation.label}
            </Chip>
          ))}
        </ChipRow>
        <Group justify={total > 1 ? 'space-between' : 'flex-end'}>
          {isLast ? (
            <Button
              size="compact-sm"
              disabled={!canSend || locked}
              onClick={() => onSend?.()}
            >
              Send
            </Button>
          ) : (
            <Button
              size="compact-sm"
              disabled={!situationId || locked}
              onClick={advance}
            >
              Next
            </Button>
          )}
        </Group>
        <StepProgress step={step} total={total} />
      </Stack>
    );
  }

  if (current?.kind === 'field') {
    const groups = groupFields(visible.filter((f) => f.step === current.step));
    return (
      <Stack gap={rem(16)} mt={rem(12)}>
        {groups.map((group) => (
          <Stack key={`${group.step}::${group.group}`} gap={rem(10)}>
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">
              {group.group}
            </Text>
            {group.fields.map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                value={answers[field.key]}
                disabled={locked}
                onChange={(value) => setAnswer(field.key, value)}
              />
            ))}
          </Stack>
        ))}
        <Group justify={step === 0 ? 'flex-end' : 'space-between'}>
          {step > 0 && (
            <Button
              variant="default"
              size="compact-sm"
              disabled={locked}
              onClick={goBack}
            >
              Back
            </Button>
          )}
          {isLast ? (
            <Button
              size="compact-sm"
              disabled={!canSend || locked}
              onClick={() => onSend?.()}
            >
              Send
            </Button>
          ) : (
            <Button size="compact-sm" disabled={locked} onClick={advance}>
              Next
            </Button>
          )}
        </Group>
        <StepProgress step={step} total={total} />
      </Stack>
    );
  }

  return (
    <Stack gap={rem(12)} mt={rem(12)}>
      <Text size="sm" c="dimmed">
        Nothing to report yet.
      </Text>
    </Stack>
  );
}

/** Renders the "details" phase: every group on one view, prefilled from the
 *  existing claim's answers, ending in the same Send. */
function DetailsPhase({
  fields,
  initialAnswers,
  locked,
  onSend,
  onWrite,
}: {
  readonly fields: readonly ClaimReportField[];
  readonly initialAnswers: ClaimReportAnswers;
  readonly locked: boolean;
  readonly onSend: (() => void) | undefined;
  readonly onWrite: (path: string, value: unknown) => void;
}) {
  const { answers, situationId, setAnswer } = useClaimReportAnswers(
    fields,
    initialAnswers,
    onWrite
  );
  const visible = visibleFields(fields, answers, situationId);
  const groups = groupFields(visible);
  const canSend = hasAnyAnswer(answers);

  return (
    <Stack gap={rem(16)} mt={rem(12)}>
      {groups.map((group) => (
        <Stack key={`${group.step}::${group.group}`} gap={rem(10)}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            {group.group}
          </Text>
          {group.fields.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={answers[field.key]}
              disabled={locked}
              onChange={(value) => setAnswer(field.key, value)}
            />
          ))}
        </Stack>
      ))}

      <Group justify="flex-end">
        <Button
          size="compact-sm"
          disabled={!canSend || locked}
          onClick={() => onSend?.()}
        >
          Send
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * A2UI `ClaimReport` card: a suggestions-only surface for reporting an
 * insurance claim (FNOL), or adding more answers to one already open. No
 * TextInput/Textarea/NumberInput/money input, upload control, attachment
 * list, or review/confirmation step anywhere — every answer is a chip, a
 * radio card, or a date/time pick, and the card's one action is a
 * chat-send: the picks re-enter the conversation as the user's own message,
 * exactly like `AskQuestions:SubmitAnswers`. The card never calls a backend
 * itself.
 *
 * Two phases: `choices` (new report, situation then field steps) and
 * `details` (more answers for an already-open claim, one view). Both freeze
 * once `/locked` is set — Send fires it optimistically, and a stale turn
 * fires it too — showing the picks the same way AskQuestions freezes after
 * submit.
 */
export function ClaimReport({
  phase,
  title,
  policy,
  situations,
  fields,
  onSend,
  initialAnswers,
  onWrite,
  locked,
  submitted,
}: ClaimReportProps) {
  const isLocked = locked === true;
  // The binder always supplies onWrite; this default only guards a bare
  // presentational render (a story, a test that doesn't care about writes).
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const write = onWrite ?? (() => {});
  const resolvedFields = fields ?? [];
  const resolvedSituations = situations ?? [];
  const resolvedAnswers = initialAnswers ?? {};
  const situationIdForFreeze =
    typeof resolvedAnswers.situationId === 'string'
      ? resolvedAnswers.situationId
      : undefined;

  return (
    <Paper
      radius="md"
      p="md"
      withBorder
      style={{
        width: '100%',
        borderColor: SURFACE_COLORS.cardBorder,
        backgroundColor: 'var(--mantine-color-body)',
      }}
    >
      <SurfaceHeader padding="md" below="none">
        <Text fw={700} size="md" lh={1.3}>
          {title || 'Report a claim'}
        </Text>
        {policy && (
          <Text size="xs" c="dimmed" mt={rem(4)}>
            {policy.companyName} · {policy.policyNumber} · {policy.productType}
          </Text>
        )}
      </SurfaceHeader>

      {isLocked ? (
        <FrozenSummary
          submitted={submitted === true}
          situations={resolvedSituations}
          situationId={situationIdForFreeze}
          visible={visibleFields(
            resolvedFields,
            resolvedAnswers,
            situationIdForFreeze
          )}
          answers={resolvedAnswers}
        />
      ) : (
        <>
          {phase === 'choices' && (
            <ChoicesPhase
              situations={resolvedSituations}
              fields={resolvedFields}
              initialAnswers={resolvedAnswers}
              locked={isLocked}
              onSend={onSend}
              onWrite={write}
            />
          )}

          {phase === 'details' && (
            <DetailsPhase
              fields={resolvedFields}
              initialAnswers={resolvedAnswers}
              locked={isLocked}
              onSend={onSend}
              onWrite={write}
            />
          )}
        </>
      )}
    </Paper>
  );
}
